import React, { useState } from 'react';
import { Shield, Clock, MessageCircle, ChevronDown, ChevronUp, Phone, Mail, MapPin } from 'lucide-react';

const CustomerCare: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    {
      id: 1,
      question: "What happens after I invest in a property?",
      answer: "After your investment, we provide comprehensive post-investment support including regular updates, maintenance guidance, and assistance with any property-related questions. Our team remains available throughout your investment journey."
    },
    {
      id: 2,
      question: "How do you handle property maintenance and repairs?",
      answer: "We have a dedicated maintenance team that handles all property upkeep. For major repairs, we coordinate with trusted contractors and ensure all work meets our high standards. You'll receive detailed reports on all maintenance activities."
    },
    {
      id: 3,
      question: "What if I have questions about my investment?",
      answer: "We provide multiple channels for support: dedicated phone lines, email support, and scheduled check-ins. Our investment specialists are always available to discuss your portfolio and answer any concerns."
    },
    {
      id: 4,
      question: "Do you provide market analysis and investment advice?",
      answer: "Yes, we offer regular market insights, property value assessments, and investment strategy recommendations. Our team monitors market trends and provides you with data-driven insights to optimize your returns."
    },
    {
      id: 5,
      question: "How do you ensure transparency in all transactions?",
      answer: "We maintain complete transparency through detailed reporting, regular updates, and open communication. All financial transactions, property updates, and investment performance are documented and shared with our partners."
    },
    {
      id: 6,
      question: "What happens if there are issues with a property?",
      answer: "We have a 24/7 emergency response system and dedicated property managers who handle any issues immediately. Our proactive approach means we often identify and resolve potential problems before they become major issues."
    }
  ];

  const supportFeatures = [
                // Removed 24/7 Support item per requirements
    {
      icon: <Shield className="w-12 h-12 text-golden" />,
      title: "Property Management",
      description: "Comprehensive property management services including maintenance, tenant relations, and financial oversight."
    },
    {
      icon: <Clock className="w-12 h-12 text-golden" />,
      title: "Regular Updates",
      description: "Monthly reports on property performance, market conditions, and investment returns."
    },
    {
      icon: <MessageCircle className="w-12 h-12 text-golden" />,
      title: "Dedicated Advisor",
      description: "Personal investment advisor assigned to your portfolio for personalized guidance and support."
    }
  ];

  const supportChannels = [
    {
      icon: <Phone className="w-8 h-8 text-golden" />,
      title: "Phone Support",
      details: "+1 (832) 535-7744",
      description: "Speak directly with our support team"
    },
    {
      icon: <Mail className="w-8 h-8 text-golden" />,
      title: "Email Support",
      details: "goldengeneration.ip@gmail.com",
      description: "Get detailed responses within 24 hours"
    },
    {
      icon: <MapPin className="w-8 h-8 text-golden" />,
      title: "Office Visits",
      details: "By appointment only",
      description: "Schedule in-person meetings with our team"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-r from-dark-gray to-gray-800 text-white">
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 fade-in-up">Customer Care</h1>
          <p className="text-xl md:text-2xl text-golden max-w-3xl mx-auto fade-in-up stagger-1">
            Our commitment to your success extends far beyond the initial investment.
          </p>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="section-padding bg-light-beige">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4 fade-in-up">Our Commitment to You</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto fade-in-up stagger-1">
              When you partner with Golden Generation Investment Partners, you're not just making an investment—you're 
              joining a family. We believe that your success is our success, and we're committed to supporting you 
              every step of the way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch justify-items-center">
            {supportFeatures.map((feature, index) => (
              <div key={index} className={`bg-white p-6 rounded-lg shadow-lg text-center card-hover fade-in-up stagger-${index + 1} h-full w-full max-w-sm flex flex-col justify-start`}>
                <div className="mb-4 flex justify-center hover-rotate">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-dark-gray mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Post-Sale Support Section */}
      <section className="section-padding">
        <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-dark-gray mb-4">Comprehensive Post-Sale Support</h2>
              <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed">
                Our relationship doesn't end when the investment is made. We provide ongoing support 
                to ensure your investment continues to perform at its best and meets all your expectations.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-golden rounded-full flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-dark-gray">Property Management</h4>
                    <p className="text-gray-600">Full-service property management including maintenance, tenant relations, and financial oversight.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-golden rounded-full flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-dark-gray">Financial Reporting</h4>
                    <p className="text-gray-600">Monthly financial reports, tax documentation, and investment performance analysis.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-golden rounded-full flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-dark-gray">Market Insights</h4>
                    <p className="text-gray-600">Regular updates on market conditions, property values, and investment opportunities.</p>
                  </div>
                </div>
                
                {/* Removed Emergency Support block per requirements */}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop"
                alt="Customer Support"
                className="rounded-lg shadow-2xl"
              />
              {/* Removed 24/7 Support Available badge */}
            </div>
          </div>
        </div>
      </section>

      {/* Support Channels Section */}
      <section className="section-padding bg-dark-gray text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How to Reach Us</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Multiple ways to get in touch with our support team. We're here when you need us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {supportChannels.map((channel, index) => (
              <div key={index} className="bg-gray-800 p-6 rounded-lg text-center">
                <div className="mb-4 flex justify-center">{channel.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{channel.title}</h3>
                <p className="text-golden font-semibold mb-2">{channel.details}</p>
                <p className="text-gray-300 text-sm">{channel.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about our customer care and support services.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {faqs.map((faq) => (
              <div key={faq.id} className="mb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                  className="w-full bg-white p-6 rounded-lg shadow-lg text-left flex justify-between items-center hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-lg font-semibold text-dark-gray">{faq.question}</h3>
                  {openFaq === faq.id ? (
                    <ChevronUp className="w-6 h-6 text-golden" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-golden" />
                  )}
                </button>
                
                {openFaq === faq.id && (
                  <div className="bg-gray-50 p-6 rounded-b-lg border-t border-gray-200">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* We Won't Leave You Alone Section */}
      <section className="section-padding bg-golden text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">We Won't Leave You Alone</h2>
          <p className="text-xl mb-8 max-w-4xl mx-auto">
            This isn't just a tagline—it's our promise. When you invest with us, you become part of our family. 
            We're committed to your success, your peace of mind, and your long-term satisfaction. Our team is 
            always here, ready to support you, answer your questions, and ensure your investment continues to thrive.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/10 p-6 rounded-lg">
              <div className="text-3xl font-bold mb-2">100%</div>
              <div className="text-lg">Commitment to Your Success</div>
            </div>
            <div className="bg-white/10 p-6 rounded-lg">
              <div className="text-3xl font-bold mb-2">Always</div>
              <div className="text-lg">Available When You Need Us</div>
            </div>
            <div className="bg-white/10 p-6 rounded-lg">
              <div className="text-3xl font-bold mb-2">Lifetime</div>
              <div className="text-lg">Partnership & Support</div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/contact" className="bg-white text-golden hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors">
              Get Support Now
            </a>
            <a href="/developments" className="border-2 border-white text-white hover:bg-white hover:text-golden px-8 py-4 rounded-lg font-semibold transition-colors">
              View Our Projects
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerCare;
